/**
 * Shared HTTP-call telemetry helpers.
 *
 * Both the third-party `serviceFetch` wrapper and the own-backend
 * `fetchWithRefresh` wrapper need the same instrumentation: a `perf.api.call`
 * event on every request, `error.api.4xx` / `error.api.5xx` on bad status, and
 * `error.network` when the fetch itself throws. Extracting it here means there
 * is one implementation — and one set of privacy rules (templated paths only,
 * scrubbed messages) — instead of two that can drift apart.
 *
 * This module is config-free on purpose: the caller passes the `backend` tag
 * (`"ai"`, `"addin"`, `"integration"`), so the telemetry package never depends
 * on the app's environment config.
 */

import { logError, logEvent, scrubMessage } from "./index";
import type { Backend } from "./types";

/** High-resolution clock, falling back to Date.now() in non-DOM test envs. */
export function perfNow(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

/**
 * Emit the per-call telemetry for a completed HTTP request: always a
 * `perf.api.call`, plus an `error.api.4xx` / `error.api.5xx` on a bad status.
 * `started` is a {@link perfNow} timestamp captured before the request.
 */
export function emitApiTelemetry(
  path: string,
  method: string,
  started: number,
  statusCode: number,
  backend: Backend,
): void {
  const clientDurationMs = Math.round(perfNow() - started);
  const properties = { path, method, statusCode, backend, clientDurationMs };

  // Always emit the perf event — useful even on 2xx.
  logEvent("perf.api.call", "performance", properties);

  if (statusCode >= 500) {
    logError("error.api.5xx", { type: "HttpError", scrubbedMessage: `HTTP ${statusCode}` }, properties);
  } else if (statusCode >= 400) {
    logError("error.api.4xx", { type: "HttpError", scrubbedMessage: `HTTP ${statusCode}` }, properties);
  }
}

/** Emit an `error.network` event for a fetch that threw (DNS, offline, CORS, …).
 * A deliberate cancel (`AbortError`, e.g. Stop) is logged as `perf.api.cancelled` at info instead. */
export function emitNetworkError(
  path: string,
  method: string,
  started: number,
  backend: Backend,
  err: unknown,
): void {
  const clientDurationMs = Math.round(perfNow() - started);
  if (nameOf(err) === "AbortError") {
    logEvent("perf.api.cancelled", "info", { path, method, backend, clientDurationMs });
    return;
  }
  logError(
    "error.network",
    {
      type: nameOf(err) ?? "NetworkError",
      scrubbedMessage: scrubMessage(messageOf(err) ?? String(err)),
    },
    { path, method, backend, clientDurationMs },
  );
}

/** Strip origin, query string, and obvious id segments from a URL. */
export function sanitisePath(url: string): string {
  try {
    // The base URL is required by `new URL()` so it can parse relative URLs,
    // but it's never actually used — only `parsed.pathname` is read below.
    // Using `https://` keeps Sonar's clear-text-protocol rule satisfied
    // without changing behaviour.
    const parsed = new URL(url, "https://placeholder.invalid");
    return parsed.pathname
      // GUIDs / UUIDs in path segments become {id}
      .replace(
        /\/[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12,}/g,
        "/{id}",
      )
      // Long numeric or alphanumeric ids become {id} too
      .replace(/\/\d{4,}/g, "/{id}")
      .replace(/\/[A-Za-z0-9_-]{20,}/g, "/{id}");
  } catch {
    return "unknown";
  }
}

export function nameOf(value: unknown): string | undefined {
  if (value instanceof Error) return value.name;
  if (value && typeof value === "object" && "name" in value) {
    const n = (value as { name?: unknown }).name;
    return typeof n === "string" ? n : undefined;
  }
  return undefined;
}

export function messageOf(value: unknown): string | undefined {
  if (value instanceof Error) return value.message;
  if (value && typeof value === "object" && "message" in value) {
    const m = (value as { message?: unknown }).message;
    return typeof m === "string" ? m : undefined;
  }
  return undefined;
}
